import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  ArrowBack,
  AutoAwesome,
  Check,
  CheckCircleOutline,
  Close,
  Code,
  ErrorOutline,
  ExpandMore,
  History,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  RadioButtonUnchecked,
  Schedule,
  TrendingUp,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  completeAction,
  dismissAction,
  getActions,
  getQueueMetrics,
  snoozeAction,
  startAction,
} from '../services/actionQueueService';
import { metricsService } from '../services/metricsService';
import { ActionItem } from '../types/actionQueue.types';
import { useAuthStore } from '../store/auth.store';
import { useSiteStore } from '../store/site.store';

type MovementSnapshot = {
  previousAffectedCount: number;
  currentAffectedCount: number;
  closedCount: number;
  reopenedCount: number;
  continuingCount: number;
  changed: boolean;
  closedSampleUrls: string[];
  reopenedSampleUrls: string[];
};

type ActivityEvent = Record<string, unknown> & {
  timestamp?: string;
  eventType?: string;
  fromStatus?: string;
  toStatus?: string;
  actor?: string;
  reason?: string;
  pageMovement?: MovementSnapshot;
};

type NormalizedAction = ActionItem & {
  displayPriority: 'Critical' | 'High' | 'Medium' | 'Low';
  impactLabel: 'High' | 'Medium' | 'Low';
  effortLabel: 'High' | 'Medium' | 'Low';
  affectedLabel: string;
  sourceLabel: string;
  affectedPages: Array<Record<string, unknown>>;
  why_it_matters?: string;
  expected_impact?: string;
  evidence?: Record<string, unknown>;
  estimatedMinutes?: number;
  relatedPillar?: string;
  activityHistory: ActivityEvent[];
  latestMovement: Partial<MovementSnapshot>;
  closedCount: number;
  reopenedCount: number;
  continuingCount: number;
};

type ActionRecord = Partial<ActionItem> & Record<string, unknown>;

const textValue = (value: unknown, fallback = '') => {
  if (typeof value === 'string') return value;
  if (value == null) return fallback;
  return String(value);
};

const numberValue = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || !['[', '{'].includes(trimmed[0])) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const recordValue = (value: unknown): Record<string, unknown> | undefined => {
  const parsed = parseJsonValue(value);
  return isRecord(parsed) ? parsed : undefined;
};

const recordListValue = (value: unknown): Array<Record<string, unknown>> => {
  const parsed = parseJsonValue(value);
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isRecord);
};

const actionEvidence = (source: ActionRecord) => ({
  ...recordValue(source.evidence),
  ...recordValue(source.evidenceData),
});

const textListValue = (value: unknown): string[] => {
  const parsed = parseJsonValue(value);
  if (Array.isArray(parsed)) {
    return parsed.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (typeof parsed === 'string' && parsed.trim()) {
    return [parsed.trim()];
  }
  return [];
};

const checkEvidenceValue = (action: NormalizedAction): Record<string, unknown> => {
  const nested = recordValue(action.evidence?.checkEvidence);
  return nested || action.evidence || {};
};

const h1TextEvidence = (action: NormalizedAction) => {
  const evidence = checkEvidenceValue(action);
  return textListValue(evidence.h1Texts ?? evidence.h1_texts);
};

const evidenceText = (action: NormalizedAction, ...keys: string[]) => {
  const evidence = action.evidence || {};
  for (const key of keys) {
    const value = evidence[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const evidenceNumber = (action: NormalizedAction, ...keys: string[]) => {
  const evidence = action.evidence || {};
  for (const key of keys) {
    const parsed = Number(evidence[key]);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
};

const actionAffectedCount = (action: NormalizedAction) => {
  const directCount = Number(action.affectedCount);
  if (Number.isFinite(directCount) && directCount > 0) return directCount;
  if (action.affectedPages.length > 0) return action.affectedPages.length;
  const evidenceCount = evidenceNumber(action, 'affected_count', 'affectedCount');
  if (evidenceCount > 0) return evidenceCount;
  const labelCount = Number.parseInt(action.affectedLabel || '', 10);
  if (Number.isFinite(labelCount) && labelCount > 0) return labelCount;
  return action.pagePath ? 1 : 0;
};

const pageIdentity = (page: Record<string, unknown>) => {
  const pageId = textValue(page.pageId || page.id || page.relatedPageId || page.related_page_id).trim();
  if (pageId) return `id:${pageId}`;

  const url = textValue(page.url || page.pageUrl || page.pagePath || page.path).trim().replace(/\/$/, '').toLowerCase();
  return url ? `url:${url}` : '';
};

const actionPageIdentities = (action: NormalizedAction) => {
  const identities = new Set<string>();
  const actionRecord = action as unknown as ActionRecord;
  action.affectedPages.forEach((page) => {
    const identity = pageIdentity(page);
    if (identity) identities.add(identity);
  });

  const actionPageId = textValue(action.pageId || actionRecord.relatedPageId || actionRecord.related_page_id).trim();
  if (actionPageId) identities.add(`id:${actionPageId}`);

  const actionPagePath = textValue(action.pagePath).trim().replace(/\/$/, '').toLowerCase();
  if (actionPagePath) identities.add(`url:${actionPagePath}`);

  return identities;
};

const uniqueAffectedPageCount = (actions: NormalizedAction[]) => {
  const identities = new Set<string>();

  actions.forEach((action) => {
    const actionIdentities = actionPageIdentities(action);
    actionIdentities.forEach((identity) => identities.add(identity));
  });

  return identities.size;
};

const formatAffectedLabel = (count: number, fallback: string) => {
  if (count > 0) return `${count} ${count === 1 ? 'page' : 'pages'}`;
  return fallback;
};

const actionTypeValue = (action: NormalizedAction) =>
  String(action.actionType || action.actionTypeLabel || evidenceText(action, 'actionType') || '').toUpperCase();

const checkIdValue = (action: NormalizedAction) => evidenceText(action, 'checkId').toLowerCase();

const recommendationText = (action: NormalizedAction) =>
  evidenceText(action, 'recommendation') || action.description || 'Review the issue evidence and apply the recommended page update.';

const isHeadingAction = (action: NormalizedAction) => {
  const actionType = actionTypeValue(action);
  const checkId = checkIdValue(action);
  return actionType.includes('HEADING') || checkId.includes('h1') || h1TextEvidence(action).length > 0;
};

const normalizedStatus = (status?: string) => String(status || 'PENDING').toUpperCase();

const isOpenStatus = (status?: string) => ['PENDING', 'IN_PROGRESS', 'DISMISSED', 'DEFERRED', 'SNOOZED'].includes(normalizedStatus(status));

const isCompletedStatus = (status?: string) => ['COMPLETED', 'DONE'].includes(normalizedStatus(status));

const isAutoResolvedStatus = (status?: string) => normalizedStatus(status) === 'AUTO_RESOLVED';

type AuthStoreState = ReturnType<typeof useAuthStore.getState>;

const priorityOrder = ['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
type PriorityFilter = (typeof priorityOrder)[number];

const categoryLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizePriority = (action: ActionItem | ActionRecord): NormalizedAction['displayPriority'] => {
  const source = action as ActionRecord;
  const raw = String(source.priorityLevel || source.priority || source.severity || '').toUpperCase();
  if (raw === 'CRITICAL') return 'Critical';
  if (raw === 'HIGH') return 'High';
  if (raw === 'LOW') return 'Low';
  const numeric = Number(source.priority);
  if (!Number.isNaN(numeric)) {
    if (numeric <= 1) return 'Critical';
    if (numeric === 2) return 'High';
    if (numeric === 3) return 'Medium';
    return 'Low';
  }
  return 'Medium';
};

const normalizeEffort = (value: unknown): NormalizedAction['effortLabel'] => {
  const raw = String(value || '').toUpperCase();
  if (raw.includes('HIGH')) return 'High';
  if (raw.includes('LOW')) return 'Low';
  return 'Medium';
};

const normalizeAction = (action: ActionItem | ActionRecord): NormalizedAction => {
  const source = action as ActionRecord;
  const priority = normalizePriority(source);
  const effort = normalizeEffort(source.effort || source.effort_level || source.effortLevel);
  const impact = normalizeEffort(source.expected_impact || source.expectedImpact || (priority === 'Critical' || priority === 'High' ? 'High' : priority === 'Low' ? 'Low' : 'Medium'));
  const pageUrl = textValue(source.pageUrl || source.page_url || source.pagePath);
  const fallbackPriorityScore = Math.max(20, 100 - numberValue(source.priority, 3) * 18);
  const evidence = actionEvidence(source);
  const affectedPages = recordListValue(source.affectedPages || evidence.affected_pages || evidence.affectedPages);
  const evidenceAffectedCount = Number(source.affectedCount ?? evidence?.affected_count ?? evidence?.affectedCount ?? affectedPages.length ?? 0);
  const affected = source.affectedLabel || (evidenceAffectedCount > 0 ? formatAffectedLabel(evidenceAffectedCount, 'Site') : pageUrl ? '1 page' : source.related_page_id ? '1 page' : 'Site');

  const rawActivityHistory = recordListValue(source.activityHistory ?? evidence?.activityHistory) as ActivityEvent[];
  const rawLatestMovement = (isRecord(source.latestMovement) ? source.latestMovement : isRecord(evidence?.latestMovement) ? evidence?.latestMovement : {}) as Partial<MovementSnapshot>;

  return {
    ...source,
    id: textValue(source.id),
    title: textValue(source.title, 'Untitled action'),
    description: textValue(source.description || source.rationale || source.why_it_matters, 'Review and resolve this recommendation.'),
    category: textValue(source.category || source.related_pillar, 'AI_SEARCHABILITY') as ActionItem['category'],
    priority: priorityKey(priority) as ActionItem['priority'],
    priorityScore: numberValue(source.priorityScore ?? source.priority_score, fallbackPriorityScore),
    status: String(source.status || 'PENDING').toUpperCase() as ActionItem['status'],
    pagePath: pageUrl || undefined,
    effort: (String(effort).toUpperCase() as ActionItem['effort']) || 'MEDIUM',
    createdAt: textValue(source.createdAt || source.created_at, new Date().toISOString()),
    updatedAt: textValue(source.updatedAt || source.updated_at, new Date().toISOString()),
    isDuplicate: Boolean(source.isDuplicate),
    notes: source.notes ? String(source.notes) : undefined,
    evidence,
    why_it_matters: textValue(source.why_it_matters || source.whyItMatters || source.rationale),
    expected_impact: textValue(source.expected_impact || source.expectedImpact),
    displayPriority: priority,
    impactLabel: impact,
    effortLabel: effort,
    affectedLabel: String(affected),
    affectedPages,
    sourceLabel: textValue(source.sourceLabel || source.source || evidence?.source_label, 'Crawler'),
    activityHistory: rawActivityHistory,
    latestMovement: rawLatestMovement,
    closedCount: numberValue(source.closedCount ?? rawLatestMovement.closedCount, 0),
    reopenedCount: numberValue(source.reopenedCount ?? rawLatestMovement.reopenedCount, 0),
    continuingCount: numberValue(source.continuingCount ?? rawLatestMovement.continuingCount, 0),
  };
};

const priorityKey = (priority: NormalizedAction['displayPriority']) => priority.toUpperCase() as Exclude<PriorityFilter, 'ALL'>;

const statusText = (status?: string) => {
  const raw = normalizedStatus(status);
  if (raw === 'IN_PROGRESS') return 'In progress';
  if (raw === 'COMPLETED' || raw === 'DONE') return 'Complete';
  if (raw === 'DISMISSED') return 'Dismissed';
  if (raw === 'AUTO_RESOLVED') return 'Auto-resolved';
  if (raw === 'ARCHIVED') return 'Archived';
  if (raw === 'DEFERRED' || raw === 'SNOOZED') return 'Snoozed';
  return 'Pending';
};

const severityColor = (priority: NormalizedAction['displayPriority']) => {
  if (priority === 'Critical') return '#e43d30';
  if (priority === 'High') return '#f47b20';
  if (priority === 'Medium') return '#f7b731';
  return '#31a66a';
};

const signalDots = (level: 'High' | 'Medium' | 'Low', color: string) => {
  const active = level === 'High' ? 3 : level === 'Medium' ? 2 : 1;
  return (
    <Box sx={{ display: 'flex', gap: 0.45, mt: 0.5 }}>
      {[0, 1, 2, 3, 4].map((dot) => (
        <Box
          key={dot}
          sx={{
            width: 9,
            height: 4,
            borderRadius: 5,
            bgcolor: dot < active ? color : '#e6eaf0',
          }}
        />
      ))}
    </Box>
  );
};

const formatDate = (value?: string) => {
  if (!value) return 'Jun 10, 2026 00:00';
  try {
    return format(new Date(value), 'MMM d, yyyy HH:mm');
  } catch {
    return 'Jun 10, 2026 00:00';
  }
};

const codeExample = (kind: 'organization' | 'article') => {
  if (kind === 'organization') {
    return `{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Your Organization",
  "url": "https://example.com",
  "logo": "https://example.com/logo.png"
}`;
  }

  return `{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Your Article Title",
  "author": { "@type": "Person", "name": "Author Name" },
  "datePublished": "2026-06-10"
}`;
};

const buildHowToFixSteps = (action: NormalizedAction) => {
  const actionType = actionTypeValue(action);
  const recommendation = recommendationText(action);
  const h1Texts = h1TextEvidence(action);

  if (isHeadingAction(action)) {
    return [
      h1Texts.length > 0
        ? `Review the crawler-observed H1 tags: ${h1Texts.map((text) => `"${text}"`).join(', ')}.`
        : 'Review the latest crawl evidence for missing or duplicate H1 tags.',
      recommendation,
      h1Texts.length > 1 ? 'Keep the primary page topic as the only H1 and convert duplicate H1 headings to H2 or H3.' : 'Add one visible H1 that matches the primary page topic.',
      'Publish the page update and rerun the HTML extraction/Page Readiness job to confirm the H1 check is resolved.',
    ];
  }

  if (actionType.includes('CANONICAL')) {
    return [
      'Inspect the canonical value captured by the latest crawl.',
      recommendation,
      'Set a single absolute canonical URL that matches the preferred indexable page.',
      'Re-crawl the page and confirm the canonical check passes.',
    ];
  }

  if (actionType.includes('STRUCTURED_DATA') || actionType.includes('SEMANTIC') || actionType.includes('SCHEMA')) {
    return [
      'Review the missing or invalid structured-data signal for the affected page.',
      recommendation,
      'Add schema that matches the visible page content and validate it before publishing.',
      'Rerun the crawl/readiness checks and verify the structured-data issue is cleared.',
    ];
  }

  if (actionType.includes('ROBOTS') || actionType.includes('CRAWL') || actionType.includes('INDEX')) {
    return [
      'Review the latest crawlability evidence for robots, noindex, sitemap, status, and canonical signals.',
      recommendation,
      'Apply the crawl/indexing change only to pages that should be discoverable.',
      'Run a fresh crawl and validate the page readiness status after the change.',
    ];
  }

  return [
    'Review the affected page and the latest issue evidence.',
    recommendation,
    action.expected_impact ? `Prioritize this because the expected impact is ${action.expected_impact}.` : `Use the ${action.sourceLabel} evidence to make the smallest page update that resolves the condition.`,
    'Publish the change and validate the action after the next crawl completes.',
  ];
};

const buildReferences = (action: NormalizedAction) => {
  const sourceTable = evidenceText(action, 'source_table');
  const condition = evidenceText(action, 'condition');
  const references = [
    sourceTable ? `Source data: ${sourceTable}` : `Source data: ${action.sourceLabel}`,
  ];
  if (condition) references.push(`Detected condition: ${condition}`);
  if (isHeadingAction(action)) {
    references.push('Crawler evidence: latest HTML extraction H1 count and H1 text list');
    references.push('Search guidance: use one clear H1 for the primary page topic');
  } else if (actionTypeValue(action).includes('STRUCTURED_DATA')) {
    references.push('Schema.org structured data reference');
    references.push('Google rich result and structured data validation guidance');
  } else {
    references.push('Page Readiness taxonomy for this action scope');
    references.push('Latest internal crawl evidence for the affected page');
  }
  return references;
};

const actionActivityHistory = (action: NormalizedAction): ActivityEvent[] => {
  const entries: ActivityEvent[] = action.activityHistory.length > 0
    ? action.activityHistory
    : recordListValue(action.evidence?.activityHistory ?? action.evidence?.activity_history) as ActivityEvent[];
  return [...entries].sort((left, right) => {
    const leftTime = new Date(textValue(left.timestamp || left.at || '')).getTime();
    const rightTime = new Date(textValue(right.timestamp || right.at || '')).getTime();
    return leftTime - rightTime;
  });
};

const movementChips = (movement: Partial<MovementSnapshot>) => {
  const chips: Array<{ label: string; color: string; bg: string }> = [];
  if ((movement.closedCount ?? 0) > 0)
    chips.push({ label: `${movement.closedCount} closed`, color: '#166534', bg: '#dcfce7' });
  if ((movement.reopenedCount ?? 0) > 0)
    chips.push({ label: `${movement.reopenedCount} reopened`, color: '#92400e', bg: '#fef3c7' });
  if ((movement.continuingCount ?? 0) > 0)
    chips.push({ label: `${movement.continuingCount} continuing`, color: '#1e3a5f', bg: '#e0eaff' });
  return chips;
};

const historyTitleFor = (entry: Record<string, unknown>) => {
  const eventType = textValue(entry.eventType).toUpperCase();
  const fromStatus = statusText(textValue(entry.fromStatus));
  const toStatus = statusText(textValue(entry.toStatus));
  const reason = textValue(entry.reason);
  switch (eventType) {
    case 'CREATED':
      return textValue(entry.title, 'Action created');
    case 'STARTED':
      return 'Work started';
    case 'COMPLETED':
      return 'Action marked complete';
    case 'DISMISSED':
      return reason ? `Action dismissed: ${reason}` : 'Action dismissed';
    case 'SNOOZED': {
      const until = textValue(entry.snoozedUntil);
      return until ? `Action snoozed until ${formatDate(until)}` : 'Action snoozed';
    }
    case 'RESTORED':
      return 'Action restored to pending';
    case 'AUTO_RESOLVED':
      return 'Issue no longer detected; auto-resolved';
    case 'ARCHIVED':
      return 'Action archived';
    default:
      if (fromStatus && toStatus) return `Status changed: ${fromStatus} -> ${toStatus}`;
      return textValue(entry.title, 'Action updated');
  }
};

const historyIconFor = (entry: Record<string, unknown>) => {
  const eventType = textValue(entry.eventType).toUpperCase();
  switch (eventType) {
    case 'CREATED':
      return <RadioButtonUnchecked />;
    case 'COMPLETED':
      return <CheckCircleOutline />;
    case 'DISMISSED':
      return <Close />;
    default:
      return <History />;
  }
};

const buildHistoryEvents = (action: NormalizedAction) => {
  const metadataHistory = actionActivityHistory(action);
  if (metadataHistory.length > 0) {
    const events = metadataHistory.map((entry) => ({
      icon: historyIconFor(entry),
      title: historyTitleFor(entry),
      date: formatDate(textValue(entry.timestamp || entry.at || action.updatedAt)),
      movement: (entry.pageMovement as Partial<MovementSnapshot>) ?? {},
    }));
    if (!metadataHistory.some((entry) => textValue(entry.eventType).toUpperCase() === 'CREATED')) {
      events.unshift({
        icon: <RadioButtonUnchecked />,
        title: `Action created from ${action.sourceLabel}`,
        date: formatDate(action.createdAt),
        movement: {},
      });
    }
    return events;
  }

  const condition = evidenceText(action, 'condition');
  const events = [
    { icon: <RadioButtonUnchecked />, title: `Action created from ${action.sourceLabel}`, date: formatDate(action.createdAt), movement: {} as Partial<MovementSnapshot> },
    { icon: <History />, title: condition ? `Condition detected: ${condition}` : `${action.displayPriority} priority assigned from issue evidence`, date: formatDate(action.updatedAt), movement: action.latestMovement },
  ];
  const status = String(action.status || '').toUpperCase();
  if (status === 'COMPLETED' || status === 'DONE') {
    events.push({ icon: <CheckCircleOutline />, title: 'Action marked complete', date: formatDate(action.completedAt || action.updatedAt), movement: {} });
  } else if (status === 'IN_PROGRESS') {
    events.push({ icon: <TrendingUp />, title: 'Work in progress', date: 'Current', movement: action.latestMovement });
  } else if (status === 'DISMISSED') {
    events.push({ icon: <Close />, title: action.dismissalReason || action.dismissed_reason || 'Action dismissed', date: 'Current', movement: {} });
  } else {
    events.push({ icon: <TrendingUp />, title: 'Waiting for the page update and fresh crawl validation', date: 'Current', movement: action.latestMovement });
  }
  return events;
};

const ActionWorkspacePage: React.FC = () => {
  const { actionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((state: AuthStoreState) => state.user?.tenantId || '1');
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const setSites = useSiteStore((state) => state.setSites);
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [showAllOpenActions, setShowAllOpenActions] = useState(false);
  const [tab, setTab] = useState(0);
  const [sort, setSort] = useState('priority');

  const { data: sites = [] } = useQuery({
    queryKey: ['action-workspace-sites', tenantId],
    queryFn: metricsService.getSites,
    staleTime: 30 * 60 * 1000,
  });

  React.useEffect(() => {
    if (sites.length > 0) {
      setSites(sites);
    }
  }, [setSites, sites]);

  const effectiveSite = selectedSite || sites[0];

  const { data: actionData = [], isLoading, error } = useQuery({
    queryKey: ['action-workspace-actions', tenantId, effectiveSite?.id],
    queryFn: () => getActions({ siteId: effectiveSite.id, status: ['PENDING', 'IN_PROGRESS', 'DISMISSED', 'COMPLETED', 'AUTO_RESOLVED', 'ARCHIVED'], excludeLocalBusiness: true }, tenantId),
    enabled: !!tenantId && !!effectiveSite?.id,
    refetchInterval: 60000,
  });

  const { data: metrics } = useQuery({
    queryKey: ['action-workspace-metrics', tenantId],
    queryFn: () => getQueueMetrics(tenantId),
    enabled: !!tenantId,
    refetchInterval: 60000,
  });

  const normalizedActions = useMemo(
    () => actionData.map(normalizeAction),
    [actionData]
  );

  const filteredActions = useMemo(() => {
    const filtered = normalizedActions.filter((action) =>
      isOpenStatus(action.status) &&
      (priorityFilter === 'ALL' ? true : priorityKey(action.displayPriority) === priorityFilter) &&
      (!showAllOpenActions || categoryFilter === 'ALL' ? true : String(action.category || '').toUpperCase() === categoryFilter)
    );
    return [...filtered].sort((left, right) => {
      if (sort === 'newest') {
        return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
      }
      return Number(left.priority || 4) - Number(right.priority || 4);
    });
  }, [categoryFilter, normalizedActions, priorityFilter, showAllOpenActions, sort]);

  const categoryOptions = useMemo(() => {
    const categories = new Map<string, { label: string; count: number }>();
    normalizedActions.filter((action) => isOpenStatus(action.status)).forEach((action) => {
      const key = String(action.category || 'UNCATEGORIZED').toUpperCase();
      const current = categories.get(key) || { label: categoryLabel(key), count: 0 };
      current.count += 1;
      categories.set(key, current);
    });
    return Array.from(categories.entries()).sort(([, left], [, right]) => left.label.localeCompare(right.label));
  }, [normalizedActions]);

  const visibleActions = showAllOpenActions ? filteredActions : filteredActions.slice(0, 8);

  const selectedAction = useMemo(() => {
    if (actionId) {
      return normalizedActions.find((action) => action.id === actionId);
    }
    return undefined;
  }, [actionId, normalizedActions]);

  const detailActions = useMemo(() => {
    if (!selectedAction) return filteredActions;
    return filteredActions.some((action) => action.id === selectedAction.id) ? filteredActions : normalizedActions;
  }, [filteredActions, normalizedActions, selectedAction]);

  const selectedActionIndex = useMemo(() => {
    if (!selectedAction) return -1;
    return detailActions.findIndex((action) => action.id === selectedAction.id);
  }, [detailActions, selectedAction]);

  const previousAction = selectedActionIndex > 0 ? detailActions[selectedActionIndex - 1] : undefined;
  const nextAction = selectedActionIndex >= 0 && selectedActionIndex < detailActions.length - 1
    ? detailActions[selectedActionIndex + 1]
    : undefined;

  const counts = useMemo(() => {
    const openActions = normalizedActions.filter((action) => isOpenStatus(action.status));
    const base = { ALL: openActions.length, CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
    openActions.forEach((action) => {
      base[priorityKey(action.displayPriority)] += 1;
    });
    return base;
  }, [normalizedActions]);

  const completeMutation = useMutation({
    mutationFn: (id: string) => completeAction(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['action-workspace-actions'] }),
  });

  const startMutation = useMutation({
    mutationFn: (id: string) => startAction(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['action-workspace-actions'] }),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismissAction(id, 'Dismissed from action workspace'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['action-workspace-actions'] }),
  });

  const snoozeMutation = useMutation({
    mutationFn: (id: string) => snoozeAction(id, 7),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['action-workspace-actions'] }),
  });

  const score = metrics?.averagePriorityScore ? Math.round(metrics.averagePriorityScore) : 0;
  const activeActions = useMemo(() => normalizedActions.filter((action) => isOpenStatus(action.status)), [normalizedActions]);
  const completedActions = useMemo(() => normalizedActions.filter((action) => isCompletedStatus(action.status)), [normalizedActions]);
  const autoResolvedActions = useMemo(() => normalizedActions.filter((action) => isAutoResolvedStatus(action.status)), [normalizedActions]);
  const highPriorityOpen = activeActions.filter((action) => ['Critical', 'High'].includes(action.displayPriority));
  const completedCount = completedActions.length;
  const pagesAffected = useMemo(() => uniqueAffectedPageCount(activeActions), [activeActions]);
  const pagesImproved = useMemo(() => uniqueAffectedPageCount([...completedActions, ...autoResolvedActions]), [completedActions, autoResolvedActions]);
  const effortCompleted = completedActions.reduce((total, action) => total + numberValue(action.estimatedMinutes, 0), 0);
  const weekRangeLabel = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return `${format(start, 'MMM d')} - ${format(now, 'MMM d')}`;
  }, []);
  const completionByPillar = useMemo(() => {
    const groups = new Map<string, { label: string; completed: number; pages: number }>();
    completedActions.forEach((action) => {
      const raw = String(action.relatedPillar || action.category || 'readiness');
      const label = raw.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
      const existing = groups.get(raw) || { label, completed: 0, pages: 0 };
      existing.completed += 1;
      existing.pages += actionAffectedCount(action);
      groups.set(raw, existing);
    });
    return Array.from(groups.values()).sort((left, right) => right.completed - left.completed).slice(0, 5);
  }, [completedActions]);
  const achievements = useMemo(() => {
    const items: string[] = [];
    if (completedActions.length > 0) items.push(`Completed ${completedActions.length} action${completedActions.length === 1 ? '' : 's'} across ${pagesImproved || completedActions.length} page${(pagesImproved || completedActions.length) === 1 ? '' : 's'}.`);
    if (autoResolvedActions.length > 0) items.push(`${autoResolvedActions.length} issue${autoResolvedActions.length === 1 ? '' : 's'} auto-resolved after fresh readiness evidence.`);
    if (highPriorityOpen.length > 0) items.push(`Reduced focus to ${highPriorityOpen.length} high-priority open action${highPriorityOpen.length === 1 ? '' : 's'}.`);
    return items.length > 0 ? items : ['No completed actions yet this week. Start with the highest-priority grouped issue below.'];
  }, [autoResolvedActions.length, completedActions.length, highPriorityOpen.length, pagesImproved]);
  const nextWeekFocus = highPriorityOpen.slice(0, 4);

  if (actionId) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', color: '#0f172a', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
        {isLoading ? (
          <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : selectedAction ? (
          <Box sx={{ bgcolor: '#fff', maxWidth: 920, minHeight: '100vh', mx: 'auto', boxShadow: { md: '0 0 0 1px #e6eaf0' } }}>
            <ActionDetailPanel
              action={selectedAction}
              onBack={() => navigate('/actions-next')}
              onPrevious={previousAction ? () => navigate(`/actions-next/${previousAction.id}`) : undefined}
              onNext={nextAction ? () => navigate(`/actions-next/${nextAction.id}`) : undefined}
              onComplete={() => completeMutation.mutate(selectedAction.id)}
              onStart={() => startMutation.mutate(selectedAction.id)}
              onDismiss={() => dismissMutation.mutate(selectedAction.id)}
              onSnooze={() => snoozeMutation.mutate(selectedAction.id)}
              tab={tab}
              setTab={setTab}
            />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 4 } }}>
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/actions-next')}>Back to dashboard</Button>}>
              Action not found.
            </Alert>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', color: '#0f172a', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
      <Box sx={{ minWidth: 0, bgcolor: '#fbfcff' }}>
          <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1180, mx: 'auto' }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: 26, md: 30 }, letterSpacing: 0 }}>Action Center</Typography>
              <Typography sx={{ color: '#526174', fontSize: 13 }}>Weekly progress report, completion summary, achievements, and next actions for {weekRangeLabel}.</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load actions.</Alert>}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: '1.1fr 1.25fr 1fr 1fr' }, gap: 1.7, mb: 2.8 }}>
              <SummaryCard title="Progress Score">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 76, height: 76, borderRadius: '50%', background: `conic-gradient(#0b55bd ${score * 3.6}deg, #edf2f8 0deg)`, display: 'grid', placeItems: 'center' }}>
                    <Box sx={{ width: 58, height: 58, borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center' }}>
                      <Typography sx={{ fontSize: 25, fontWeight: 900, lineHeight: 1 }}>{score}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#7a8797', mt: -0.4 }}>/100</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#14a05d', fontWeight: 800, alignSelf: 'flex-end' }}>{completedCount} completed</Typography>
                </Box>
              </SummaryCard>
              <SummaryCard title="Open Work">
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 31, fontWeight: 900 }}>{activeActions.length}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>Open actions</Typography>
                  </Box>
                  <Stack sx={{ ml: 'auto', gap: 0.5, minWidth: 78 }}>
                    <IssueRow label="Critical" count={counts.CRITICAL} color="#ef3f3f" />
                    <IssueRow label="High" count={counts.HIGH} color="#f47b20" />
                    <IssueRow label="Medium" count={counts.MEDIUM} color="#f7b731" />
                    <IssueRow label="Low" count={counts.LOW} color="#29a66a" />
                  </Stack>
                </Box>
              </SummaryCard>
              <SummaryCard title="Pages Affected">
                <Typography sx={{ fontSize: 31, fontWeight: 900 }}>{pagesAffected}</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 800, mt: 2 }}>Across current open actions</Typography>
              </SummaryCard>
              <SummaryCard title="Completion Summary">
                <Typography sx={{ fontSize: 31, fontWeight: 900 }}>{completedCount}</Typography>
                <Typography sx={{ fontSize: 11, color: '#14a05d', fontWeight: 800, mt: 2 }}>{autoResolvedActions.length} auto-resolved</Typography>
              </SummaryCard>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' }, gap: 1.7, mb: 3 }}>
              <Paper variant="outlined" sx={{ borderColor: '#e3e8f0', borderRadius: 2, p: 2.2, bgcolor: '#fff' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 950, mb: 1 }}>Achievements This Week</Typography>
                <Stack spacing={1.1}>
                  {achievements.map((achievement) => (
                    <Box key={achievement} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <CheckCircleOutline sx={{ color: '#24a66b', fontSize: 18, mt: 0.1 }} />
                      <Typography sx={{ fontSize: 13, color: '#334155', lineHeight: 1.55 }}>{achievement}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ borderColor: '#e3e8f0', borderRadius: 2, p: 2.2, bgcolor: '#fff' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 950, mb: 1 }}>Completion by Pillar</Typography>
                {completionByPillar.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>Completed actions will appear here after work is marked complete.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {completionByPillar.map((group) => (
                      <Box key={group.label} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{group.label}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{group.pages} page{group.pages === 1 ? '' : 's'} improved</Typography>
                        </Box>
                        <Chip label={`${group.completed} done`} size="small" sx={{ height: 22, bgcolor: '#eaf7ee', color: '#167044', fontWeight: 900, fontSize: 11 }} />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Box>

            <Paper variant="outlined" sx={{ borderColor: '#e3e8f0', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', mb: 3 }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.3, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 19, fontWeight: 900, mr: 'auto' }}>Still Needs Attention</Typography>
                <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                  {priorityOrder.map((priority) => (
                    <Button
                      key={priority}
                      size="small"
                      variant={priorityFilter === priority ? 'contained' : 'outlined'}
                      onClick={() => setPriorityFilter(priority)}
                      sx={{
                        minWidth: 0,
                        px: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 800,
                        bgcolor: priorityFilter === priority ? '#eaf2ff' : '#fff',
                        color: priorityFilter === priority ? '#0b63f6' : '#3b4a5c',
                        borderColor: '#d8e0eb',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: priorityFilter === priority ? '#eaf2ff' : '#f8fafc', boxShadow: 'none' },
                      }}
                    >
                      {priority === 'ALL' ? 'All' : priority.charAt(0) + priority.slice(1).toLowerCase()}
                      <Chip label={counts[priority]} size="small" sx={{ ml: 0.7, height: 17, fontSize: 10, bgcolor: priority === 'CRITICAL' ? '#fff0f0' : priority === 'HIGH' ? '#fff6e8' : '#edf2f8' }} />
                    </Button>
                  ))}
                </Stack>
                <FormControl size="small" sx={{ minWidth: 150, ml: { md: 'auto' } }}>
                  <Select value={sort} onChange={(event) => setSort(event.target.value)} sx={{ fontSize: 12, fontWeight: 700, borderRadius: 1.4 }}>
                    <MenuItem value="priority">Sort by: Priority</MenuItem>
                    <MenuItem value="newest">Sort by: Newest</MenuItem>
                  </Select>
                </FormControl>
                {showAllOpenActions && (
                  <FormControl size="small" sx={{ minWidth: 190 }}>
                    <Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} sx={{ fontSize: 12, fontWeight: 700, borderRadius: 1.4 }}>
                      <MenuItem value="ALL">Category: All</MenuItem>
                      {categoryOptions.map(([category, option]) => (
                        <MenuItem key={category} value={category}>{option.label} ({option.count})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Divider />
              {isLoading ? (
                <Box sx={{ p: 5, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
              ) : filteredActions.length === 0 ? (
                <Stack>
                  <ActionListHeader />
                  <ActionListEmptyRow />
                </Stack>
              ) : (
                <Stack>
                  <ActionListHeader />
                  {visibleActions.map((action) => (
                    <ActionListRow
                      key={action.id}
                      action={action}
                      selected={selectedAction?.id === action.id}
                      onClick={() => navigate(`/actions-next/${action.id}`)}
                    />
                  ))}
                </Stack>
              )}
              <Box sx={{ p: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowAllOpenActions((current) => !current)}
                  sx={{ borderColor: '#e5eaf2', color: '#26364a', textTransform: 'none', fontWeight: 800, borderRadius: 1.5 }}
                >
                  {showAllOpenActions ? 'Show fewer actions' : `View all ${activeActions.length} open actions`}
                </Button>
              </Box>
            </Paper>

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 17, fontWeight: 900 }}>Next Week Focus</Typography>
              <Button variant="outlined" size="small" endIcon={<ExpandMore />} sx={{ ml: 'auto', borderRadius: 1.5, textTransform: 'none', fontWeight: 700, color: '#334155', borderColor: '#dce3ee' }}>Based on priority</Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.7 }}>
              <ProgressCard title="Actions Completed" value={String(completedCount)} note={`${effortCompleted} min estimated effort`} />
              <ProgressCard title="Pages Improved" value={String(pagesImproved)} note={`${autoResolvedActions.length} auto-resolved`} />
              <ProgressCard title="High-Priority Open" value={String(highPriorityOpen.length)} note={nextWeekFocus[0]?.title || 'No high-priority focus item'} />
            </Box>
          </Box>
      </Box>
    </Box>
  );
};

const SummaryCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper variant="outlined" sx={{ bgcolor: '#fff', borderColor: '#e3e8f0', borderRadius: 2, p: 2.1, minHeight: 132 }}>
    <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 800, mb: 1.6 }}>{title}</Typography>
    {children}
  </Paper>
);

const IssueRow: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, justifyContent: 'space-between' }}>
    <Typography sx={{ fontSize: 11, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontSize: 11, color, fontWeight: 900 }}>{count}</Typography>
  </Box>
);

const actionListColumns = { xs: '34px 1fr 22px', md: '34px minmax(210px, 1fr) 76px 84px 76px 76px 76px 22px' };

const ActionListHeader: React.FC = () => (
  <Box
    sx={{
      px: 2,
      py: 1.1,
      display: 'grid',
      gridTemplateColumns: actionListColumns,
      alignItems: 'center',
      gap: 1.4,
      bgcolor: '#f8fafc',
      borderTop: '1px solid #eef2f7',
    }}
  >
    <Box />
    <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Action</Typography>
    <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 11, color: '#64748b', fontWeight: 900 }}>Priority</Typography>
    <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 11, color: '#64748b', fontWeight: 900 }}>Affected</Typography>
    <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 11, color: '#64748b', fontWeight: 900 }}>Impact</Typography>
    <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 11, color: '#64748b', fontWeight: 900 }}>Effort</Typography>
    <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 11, color: '#64748b', fontWeight: 900 }}>Status</Typography>
    <Box />
  </Box>
);

const ActionListEmptyRow: React.FC = () => (
  <Box
    sx={{
      px: 2,
      py: 1.65,
      display: 'grid',
      gridTemplateColumns: actionListColumns,
      alignItems: 'center',
      gap: 1.4,
      borderTop: '1px solid #eef2f7',
      bgcolor: '#fff',
    }}
  >
    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#e6f7fb', color: '#0f8ca8', display: 'grid', placeItems: 'center', fontSize: 13 }}>i</Box>
    <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 700 }}>No actions match this filter.</Typography>
    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    <Box />
  </Box>
);

const ActionListRow: React.FC<{ action: NormalizedAction; selected: boolean; onClick: () => void }> = ({ action, selected, onClick }) => {
  const color = severityColor(action.displayPriority);
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.65,
        display: 'grid',
        gridTemplateColumns: actionListColumns,
        alignItems: 'center',
        gap: 1.4,
        borderTop: '1px solid #eef2f7',
        bgcolor: selected ? '#f8fbff' : '#fff',
        cursor: 'pointer',
        '&:hover': { bgcolor: '#f8fbff' },
      }}
    >
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: `${color}18`, color, display: 'grid', placeItems: 'center' }}>
        <ErrorOutline sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: '#172033' }} noWrap>{action.title}</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.45 }} noWrap>{action.description}</Typography>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.6, mt: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={action.affectedScope === 'site' ? 'Site level' : 'Page level'} size="small" sx={{ height: 19, fontSize: 10, bgcolor: '#f1f5f9', color: '#526174', fontWeight: 700 }} />
          {action.closedCount > 0 && <Chip label={`${action.closedCount} closed`} size="small" sx={{ height: 19, fontSize: 10, bgcolor: '#dcfce7', color: '#166534', fontWeight: 800 }} />}
          {action.reopenedCount > 0 && <Chip label={`${action.reopenedCount} reopened`} size="small" sx={{ height: 19, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 800 }} />}
          {action.continuingCount > 0 && <Chip label={`${action.continuingCount} continuing`} size="small" sx={{ height: 19, fontSize: 10, bgcolor: '#e0eaff', color: '#1e3a5f', fontWeight: 800 }} />}
        </Box>
      </Box>
      <PriorityCell priority={action.displayPriority} color={color} hideOnMobile />
      <MetricCell label="Affected" value={action.affectedLabel} hideOnMobile />
      <MiniSignal label="Impact" level={action.impactLabel} color={severityColor(action.displayPriority)} hideOnMobile />
      <MiniSignal label="Effort" level={action.effortLabel} color={action.effortLabel === 'Low' ? '#24a66b' : '#f7b731'} hideOnMobile />
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Typography sx={{ fontSize: 10, color: '#64748b' }}>Status</Typography>
        <Chip label={statusText(action.status)} size="small" sx={{ height: 22, fontSize: 11, bgcolor: '#eaf2ff', color: '#0b63f6', fontWeight: 800 }} />
      </Box>
      <KeyboardArrowRight sx={{ color: '#94a3b8', fontSize: 19 }} />
    </Box>
  );
};

const MetricCell: React.FC<{ label: string; value: string; hideOnMobile?: boolean }> = ({ label, value, hideOnMobile }) => (
  <Box sx={{ display: hideOnMobile ? { xs: 'none', md: 'block' } : 'block' }}>
    <Typography sx={{ fontSize: 10, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#172033' }}>{value}</Typography>
  </Box>
);

const PriorityCell: React.FC<{ priority: NormalizedAction['displayPriority']; color: string; hideOnMobile?: boolean }> = ({ priority, color, hideOnMobile }) => (
  <Box sx={{ display: hideOnMobile ? { xs: 'none', md: 'block' } : 'block' }}>
    <Typography sx={{ fontSize: 10, color: '#64748b' }}>Priority</Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 800, color }}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, display: 'inline-block', mr: 0.5 }} />
      {priority}
    </Typography>
  </Box>
);

const MiniSignal: React.FC<{ label: string; level: 'High' | 'Medium' | 'Low'; color: string; hideOnMobile?: boolean }> = ({ label, level, color, hideOnMobile }) => (
  <Box sx={{ display: hideOnMobile ? { xs: 'none', md: 'block' } : 'block' }}>
    <Typography sx={{ fontSize: 10, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#172033' }}><Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, display: 'inline-block', mr: 0.5 }} />{level}</Typography>
    {signalDots(level, color)}
  </Box>
);

const ProgressCard: React.FC<{ title: string; value: string; note: string }> = ({ title, value, note }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: '#e3e8f0', p: 2, bgcolor: '#fff', overflow: 'hidden' }}>
    <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 800 }}>{title}</Typography>
    <Typography sx={{ fontSize: 25, fontWeight: 900, mt: 1 }}>{value}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', mt: 1.5, gap: 1 }}>
      <Typography sx={{ fontSize: 11, color: '#14a05d', fontWeight: 800 }}>{note}</Typography>
      <Box sx={{ ml: 'auto', width: 88, height: 28, borderBottom: '2px solid #2d69ff', borderRight: '2px solid #2d69ff', borderRadius: '50% 0 6px 0', transform: 'skewX(-20deg)' }} />
    </Box>
  </Paper>
);

const ActionDetailPanel: React.FC<{
  action: NormalizedAction;
  onBack: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onComplete: () => void;
  onStart: () => void;
  onDismiss: () => void;
  onSnooze: () => void;
  tab: number;
  setTab: (value: number) => void;
}> = ({ action, onBack, onPrevious, onNext, onComplete, onStart, onDismiss, onSnooze, tab, setTab }) => {
  const color = severityColor(action.displayPriority);
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ height: 58, borderBottom: '1px solid #edf1f6', display: 'flex', alignItems: 'center', px: 2.5, gap: 1 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: 'none', color: '#526174', fontWeight: 800, fontSize: 12 }}>Back to dashboard</Button>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title="Previous issue">
            <span>
              <IconButton size="small" onClick={onPrevious} disabled={!onPrevious} sx={{ border: '1px solid #dce3ee', borderRadius: 1.2 }}>
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next issue">
            <span>
              <IconButton size="small" onClick={onNext} disabled={!onNext} sx={{ border: '1px solid #dce3ee', borderRadius: 1.2 }}>
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onBack}><Close fontSize="small" /></IconButton>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2.2, md: 3 }, pb: 0 }}>
        <Chip label={`${action.displayPriority} Priority`} size="small" sx={{ height: 23, bgcolor: `${color}16`, color, fontSize: 11, fontWeight: 900, mb: 2 }} />
        <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 950, letterSpacing: 0, lineHeight: 1.15 }}>{action.title}</Typography>
        <Typography sx={{ color: '#526174', fontSize: 13, mt: 0.8, maxWidth: 620 }}>{action.description}</Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5, mt: 3, mb: 3 }}>
          <PriorityCell priority={action.displayPriority} color={color} />
          <MetricCell label="Affected" value={action.affectedLabel} />
          <MiniSignal label="Impact" level={action.impactLabel} color={color} />
          <MiniSignal label="Effort" level={action.effortLabel} color={action.effortLabel === 'Low' ? '#24a66b' : '#f7b731'} />
          <Box>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>Status</Typography>
            <Chip label={statusText(action.status)} size="small" sx={{ height: 22, fontSize: 11, bgcolor: '#eaf2ff', color: '#0b63f6', fontWeight: 800 }} />
          </Box>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ px: 2.2, borderBottom: '1px solid #edf1f6', minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontSize: 12, fontWeight: 900 } }}>
        <Tab label="Overview" />
        <Tab label={`Affected Pages (${action.affectedLabel.match(/\d+/)?.[0] || 1})`} />
        <Tab label="How to Fix" />
        <Tab label="References" />
        <Tab label="History" />
      </Tabs>

      <Box sx={{ p: { xs: 2.2, md: 3 }, flex: 1 }}>
        {tab === 0 && <OverviewTab action={action} />}
        {tab === 1 && <AffectedPagesTab action={action} />}
        {tab === 2 && <HowToFixTab action={action} />}
        {tab === 3 && <ReferencesTab action={action} />}
        {tab === 4 && <HistoryTab action={action} />}
      </Box>

      <Box sx={{ borderTop: '1px solid #edf1f6', p: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<Check />} onClick={onComplete} sx={{ bgcolor: '#0b63f6', borderRadius: 1.2, textTransform: 'none', fontWeight: 900, px: 2.5 }}>Mark as Complete</Button>
        <Button variant="outlined" startIcon={<Schedule />} onClick={onSnooze} sx={{ borderColor: '#dce3ee', color: '#26364a', borderRadius: 1.2, textTransform: 'none', fontWeight: 800 }}>Snooze</Button>
        <Button variant="outlined" startIcon={<AddCircleOutline />} onClick={onStart} sx={{ borderColor: '#dce3ee', color: '#26364a', borderRadius: 1.2, textTransform: 'none', fontWeight: 800 }}>Start</Button>
        <Button variant="outlined" onClick={onDismiss} sx={{ borderColor: '#dce3ee', color: '#26364a', borderRadius: 1.2, textTransform: 'none', fontWeight: 800, ml: { md: 'auto' } }}>More actions</Button>
      </Box>
    </Box>
  );
};

const OverviewTab: React.FC<{ action: NormalizedAction }> = ({ action }) => (
  <Stack spacing={3}>
    {h1TextEvidence(action).length > 0 && (
      <Box sx={{ border: '1px solid #bfd7ff', bgcolor: '#f5f9ff', borderRadius: 2, p: 2 }}>
        <Typography sx={{ fontWeight: 950, color: '#0b55bd', fontSize: 15, mb: 1 }}>Crawler-observed H1 texts</Typography>
        <Stack spacing={0.8}>
          {h1TextEvidence(action).map((text, index) => (
            <Box key={`${text}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Chip label={`H1 ${index + 1}`} size="small" color={index === 0 ? 'primary' : 'warning'} sx={{ height: 22, fontSize: 11, fontWeight: 800 }} />
              <Typography sx={{ color: '#26364a', fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word' }}>{text}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    )}

    <Box sx={{ border: '1px solid #b8e5ca', bgcolor: '#f0fbf4', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontWeight: 950, color: '#166534', fontSize: 15 }}>Why this matters</Typography>
        <AutoAwesome sx={{ fontSize: 18, color: '#24a66b', ml: 'auto' }} />
      </Box>
      <Typography sx={{ color: '#166534', fontSize: 13, lineHeight: 1.65 }}>
        {action.why_it_matters || action.rationale || evidenceText(action, 'recommendation') || 'This action is based on the latest readiness evidence for the affected page.'}
      </Typography>
      {evidenceText(action, 'condition') && (
        <Typography sx={{ color: '#526174', fontSize: 12.5, mt: 2, lineHeight: 1.6 }}>
          Detected condition: {evidenceText(action, 'condition')}
        </Typography>
      )}
    </Box>

    <Box>
      <Typography sx={{ fontWeight: 950, mb: 1, fontSize: 15 }}>What to do</Typography>
      <Typography sx={{ color: '#334155', fontSize: 13.2, mb: 2 }}>{recommendationText(action)}</Typography>
      {(actionTypeValue(action).includes('STRUCTURED_DATA') || actionTypeValue(action).includes('SCHEMA')) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          <CodeCard title="Organization (Example)" code={codeExample('organization')} />
          <CodeCard title="Article (Example)" code={codeExample('article')} />
        </Box>
      )}
    </Box>

    <Box>
      <Typography sx={{ fontWeight: 950, mb: 1, fontSize: 15 }}>Check after fix</Typography>
      <Stack spacing={0.8}>
        <CheckLine text="Re-crawl the affected page after publishing the update" />
        <CheckLine text={`Confirm the ${evidenceText(action, 'checkId') || action.actionType || 'readiness'} check no longer appears as FAIL or WARNING`} />
      </Stack>
    </Box>

    {(action.closedCount > 0 || action.reopenedCount > 0 || action.continuingCount > 0) && (
      <Box sx={{ border: '1px solid #e3e8f0', borderRadius: 2, p: 2 }}>
        <Typography sx={{ fontWeight: 950, fontSize: 14, mb: 1.2 }}>Latest Page Movement</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {action.closedCount > 0 && (
            <Box sx={{ textAlign: 'center', px: 1.5, py: 1, bgcolor: '#dcfce7', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#166534', lineHeight: 1 }}>{action.closedCount}</Typography>
              <Typography sx={{ fontSize: 11, color: '#166534', fontWeight: 700, mt: 0.3 }}>Closed</Typography>
            </Box>
          )}
          {action.reopenedCount > 0 && (
            <Box sx={{ textAlign: 'center', px: 1.5, py: 1, bgcolor: '#fef3c7', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#92400e', lineHeight: 1 }}>{action.reopenedCount}</Typography>
              <Typography sx={{ fontSize: 11, color: '#92400e', fontWeight: 700, mt: 0.3 }}>Reopened</Typography>
            </Box>
          )}
          {action.continuingCount > 0 && (
            <Box sx={{ textAlign: 'center', px: 1.5, py: 1, bgcolor: '#e0eaff', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#1e3a5f', lineHeight: 1 }}>{action.continuingCount}</Typography>
              <Typography sx={{ fontSize: 11, color: '#1e3a5f', fontWeight: 700, mt: 0.3 }}>Continuing</Typography>
            </Box>
          )}
          {action.latestMovement.currentAffectedCount !== undefined && (
            <Box sx={{ textAlign: 'center', px: 1.5, py: 1, bgcolor: '#f1f5f9', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#334155', lineHeight: 1 }}>{action.latestMovement.currentAffectedCount}</Typography>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mt: 0.3 }}>Current</Typography>
            </Box>
          )}
        </Box>
      </Box>
    )}

    <Divider />
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5 }}>
      <MetricCell label="Created" value={formatDate(action.createdAt)} />
      <MetricCell label="Category" value={String(action.category).replace(/_/g, ' ')} />
      <MetricCell label="Source" value={action.sourceLabel} />
      <MetricCell label="ID" value={action.id} />
      <MetricCell label="Updated" value={formatDate(action.updatedAt)} />
    </Box>
  </Stack>
);

const CodeCard: React.FC<{ title: string; code: string }> = ({ title, code }) => (
  <Paper variant="outlined" sx={{ borderColor: '#dfe6ef', borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid #edf1f6' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 900 }}>{title}</Typography>
    </Box>
    <Box component="pre" sx={{ m: 0, p: 1.5, fontSize: 11.5, lineHeight: 1.65, color: '#334155', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', bgcolor: '#fbfdff' }}>
      {code}
    </Box>
  </Paper>
);

const CheckLine: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
    <CheckCircleOutline sx={{ color: '#24a66b', fontSize: 17 }} />
    <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{text}</Typography>
  </Box>
);

const AffectedPagesTab: React.FC<{ action: NormalizedAction }> = ({ action }) => {
  const [evidencePage, setEvidencePage] = useState<Record<string, unknown> | null>(null);
  const affectedPages = action.affectedPages.length > 0
    ? action.affectedPages
    : action.pagePath
      ? [{ url: action.pagePath, status: action.status }]
      : [];

  return (
    <>
    <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: '#e3e8f0', overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', gap: 1, px: 2, py: 1.2, bgcolor: '#f8fafc' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Page</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Status</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Severity</Typography>
      </Box>
      {affectedPages.length === 0 ? (
        <Box sx={{ px: 2, py: 1.4, borderTop: '1px solid #edf1f6' }}>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>No affected page list was included with this action.</Typography>
        </Box>
      ) : affectedPages.map((page, index) => {
        const url = textValue(page.url || page.pageUrl || page.pagePath, 'Site-level issue');
        const hasEvidence = Object.keys(pageEvidenceEntries(page)).length > 0;
        return (
          <Box
            key={`${url}-${index}`}
            onClick={hasEvidence ? () => setEvidencePage(page) : undefined}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 120px 120px',
              gap: 1,
              px: 2,
              py: 1.4,
              borderTop: '1px solid #edf1f6',
              cursor: hasEvidence ? 'pointer' : 'default',
              '&:hover': hasEvidence ? { bgcolor: '#f8fafc' } : undefined,
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#0b63f6' }} noWrap>{url}</Typography>
            <Typography sx={{ fontSize: 13 }}>{textValue(page.status, statusText(action.status))}</Typography>
            <Typography sx={{ fontSize: 13 }}>{textValue(page.severity, '-')}</Typography>
          </Box>
        );
      })}
    </Paper>
    <PageEvidenceDialog page={evidencePage} onClose={() => setEvidencePage(null)} />
    </>
  );
};

/** analysisSurfaces lists which internal surfaces ran the check, so it is not customer evidence. */
const EVIDENCE_HIDDEN_KEYS = new Set(['analysisSurfaces', 'analysis_surfaces']);

const pageEvidenceEntries = (page: Record<string, unknown>): Record<string, unknown> => {
  const raw = page.evidence;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(([key]) => !EVIDENCE_HIDDEN_KEYS.has(key)),
  );
};

const formatEvidenceLabel = (key: string): string => key
  .replace(/_/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, (char) => char.toUpperCase());

const formatEvidenceValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length === 0 ? '-' : value.map((item) => String(item)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const PageEvidenceDialog: React.FC<{ page: Record<string, unknown> | null; onClose: () => void }> = ({ page, onClose }) => {
  const entries = page ? Object.entries(pageEvidenceEntries(page)) : [];
  const url = page ? textValue(page.url || page.pageUrl || page.pagePath, 'Site-level issue') : '';

  return (
    <Dialog open={Boolean(page)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 900 }}>Page evidence</Typography>
            <Typography sx={{ fontSize: 12.5, color: '#64748b', wordBreak: 'break-all' }}>{url}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close page evidence">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {entries.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>No evidence was recorded for this page.</Typography>
        ) : (
          <Stack spacing={1}>
            {entries.map(([key, value]) => (
              <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 1.5, alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#475569' }}>{formatEvidenceLabel(key)}</Typography>
                <Typography sx={{ fontSize: 13, color: '#0f172a', wordBreak: 'break-word' }}>{formatEvidenceValue(value)}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const HowToFixTab: React.FC<{ action: NormalizedAction }> = ({ action }) => (
  <Stack spacing={1.4}>
    {buildHowToFixSteps(action).map((step, index) => (
      <Paper key={step} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#e3e8f0', display: 'flex', gap: 1.2 }}>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#eaf2ff', color: '#0b63f6', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 900 }}>{index + 1}</Box>
        <Typography sx={{ fontSize: 13.2, color: '#334155' }}>{step}</Typography>
      </Paper>
    ))}
  </Stack>
);

const ReferencesTab: React.FC<{ action: NormalizedAction }> = ({ action }) => (
  <Stack spacing={1.2}>
    {buildReferences(action).map((item) => (
      <Paper key={item} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#e3e8f0', display: 'flex', gap: 1, alignItems: 'center' }}>
        <Code sx={{ color: '#0b63f6' }} />
        <Typography sx={{ fontSize: 13.2, fontWeight: 700 }}>{item}</Typography>
      </Paper>
    ))}
  </Stack>
);

const HistoryTab: React.FC<{ action: NormalizedAction }> = ({ action }) => (
  <Stack spacing={1.6}>
    {buildHistoryEvents(action).map((event, idx) => {
      const chips = movementChips(event.movement ?? {});
      return (
        <Box key={`${event.title}-${idx}`} sx={{ display: 'flex', gap: 1.3, alignItems: 'flex-start' }}>
          <Box sx={{ color: '#0b63f6', display: 'grid', placeItems: 'center', mt: 0.3 }}>{event.icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>{event.title}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>{event.date}</Typography>
            {chips.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, flexWrap: 'wrap' }}>
                {chips.map((chip) => (
                  <Chip
                    key={chip.label}
                    label={chip.label}
                    size="small"
                    sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: chip.bg, color: chip.color, border: 'none' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      );
    })}
  </Stack>
);

export default ActionWorkspacePage;
